import { memo, useEffect, useState } from "react";
import { onUnsavePress, onSavePress } from "@/api/srpFilter";
import { useDispatch, useSelector } from "react-redux";
import {
  setIsAddedToSaved,
  showSavedTooltip,
} from "@/redux/savedpreference/savedPreferenceSlice";
import { setSelectedProperty, updateFavorite } from "@/redux/SRP/srpSlice";
import {
  getEventCategory,
  trackApiCall,
  trackButtonClick,
} from "@/utils/analytics";
import { getSrpLocationLabel } from "@/utils/filterTrackingService";
import HeartIcon from "../../../../public/assets/svg/srp/HeartIcon";
import { setPropertyToSaveFrom } from "@/redux/login/loginSlice";
import { checkLoginStatus } from "@/api/myProfile";
import { logOut } from "@/redux/user/userSlice";

const FavoriteButton = ({
  item,
  setShowModal,
  isMapCard,
  isRecommendedProperties,
  properties,
  setProperties,
  cardIndex,
}) => {
  const dispatch = useDispatch();
  const globalPopup = useSelector((state) => state.login);
  const { selectedProperty } = useSelector((state) => state.srp);
  const userEmailId = useSelector((state) => state.user?.user?.userEmailId);
  const { srpTupples } = useSelector((state) => state.srp.srpInitData);

  const onSave = async (e) => {
    e.stopPropagation();
    e.preventDefault();

    const eventCategory = getEventCategory();
    // Get client ID from Google Analytics cookie
    const gaCookie = document.cookie
      .split("; ")
      .find((row) => row.startsWith("_ga="));
    const clientId = gaCookie ? gaCookie.split("=")[1] : "";

    // Build eventLabel: full details on SRP, minimal on recommended cards.
    const favLabel = (srpDetails) =>
      eventCategory === "NEW-SRP"
        ? srpDetails
        : `Property id: ${item?.propertyId}`;

    const response = await checkLoginStatus();
    if (response?.isLoggedOut) {
      dispatch(logOut({ isUserLoggedIn: false }));
    }

    if (globalPopup?.isUserSubscribed || !response?.isLoggedOut) {
      if (item?.onWatchList === "Y") {
        if (isRecommendedProperties) {
          const updatedFavProps =
            properties.length > 0 &&
            properties.map((property) =>
              property.propertyId === item?.propertyId
                ? { ...property, onWatchList: "" }
                : property,
            );
          setProperties(updatedFavProps);
        } else {
          const updatedSrpTupples =
            srpTupples.length > 0 &&
            srpTupples.map((tupple) =>
              tupple.propertyId === item?.propertyId
                ? { ...tupple, onWatchList: "" }
                : tupple,
            );
          dispatch(updateFavorite(updatedSrpTupples));
        }
        if (selectedProperty?.propertyId === item?.propertyId) {
          dispatch(setSelectedProperty({ ...item, onWatchList: "" }));
        }

        trackButtonClick({
          eventName: `favourite_property_unsave_click`,
          eventCategory: eventCategory,
          eventSource:
            eventCategory === "NEW-SRP"
              ? `srp_property_card`
              : `home_page_recommended`,
          eventLabel: favLabel(
            `Property id: ${item.propertyId} | propertyStatus: ${item.propertyStatus}`,
          ),
        });

        const response = await onUnsavePress({ propertyId: item?.propertyId });

        if (response === "success") {
          trackApiCall({
            eventName: `favourite_property_unsaved`,
            eventCategory: eventCategory,
            eventSource:
              eventCategory === "NEW-SRP"
                ? `srp_property_card`
                : `home_page_recommended`,
            eventLabel: favLabel(`Property id: ${item?.propertyId}`),
          });
        } else {
          trackApiCall({
            eventName: `favourite_property_unsave_error`,
            eventCategory: "Error",
            eventSource: `Error - ${
              response?.message || "Data Initialization Failed"
            }`,
            eventLabel: `Property Id: ${item.propertyId}`,
          });
        }
        dispatch(setIsAddedToSaved());
      } else if (item?.propertyId) {
        if (isRecommendedProperties) {
          const updatedFavProps =
            properties.length > 0 &&
            properties.map((property) =>
              property.propertyId === item?.propertyId
                ? { ...property, onWatchList: "Y" }
                : property,
            );
          setProperties(updatedFavProps);
        } else {
          const updatedSrpTupples =
            srpTupples.length > 0 &&
            srpTupples.map((tupple) =>
              tupple.propertyId === item?.propertyId
                ? { ...tupple, onWatchList: "Y" }
                : tupple,
            );
          dispatch(updateFavorite(updatedSrpTupples));
        }

        if (selectedProperty?.propertyId === item?.propertyId) {
          dispatch(setSelectedProperty({ ...item, onWatchList: "Y" }));
        }

        trackButtonClick({
          eventName: `favourite_property_save_click`,
          eventCategory: eventCategory,
          eventSource:
            eventCategory === "NEW-SRP"
              ? `srp_property_card`
              : `home_page_recommended`,
          eventLabel: favLabel(
            `Position: ${cardIndex} | Id: ${item.propertyId} | Category: ${item.category}| Location: ${getSrpLocationLabel()}`,
          ),
        });

        const response = await onSavePress({ propertyId: item?.propertyId });

        if (response === "success") {
          trackApiCall({
            eventName: `favourite_property_saved`,
            eventCategory: eventCategory,
            eventSource:
              eventCategory === "NEW-SRP"
                ? `srp_property_card`
                : `home_page_recommended`,
            eventLabel: favLabel(
              `Id: ${item?.propertyId} | Location: ${getSrpLocationLabel()}`,
            ),
          });
        } else {
          trackApiCall({
            eventName: `favourite_property_save_error`,
            eventCategory: "Error",
            eventSource: `Error - ${
              response?.message || "Data Initialization Failed"
            }`,
            eventLabel: `Property Id: ${item.propertyId}`,
          });
        }

        dispatch(showSavedTooltip(true));
        dispatch(setIsAddedToSaved());
      }
    } else {
      setShowModal(true);
      dispatch(setPropertyToSaveFrom(item?.propertyId));

      trackButtonClick({
        eventName: `open_quick_view_popup_click`,
        eventCategory: eventCategory,
        eventSource: `Recommended property save click`,
        eventLabel: `Position: ${
          cardIndex + 1
        } | Id: ${item.propertyId} | Category: ${item.category} | Location: ${getSrpLocationLabel()}`,
      });
    }
  };

  useEffect(() => {
    if (globalPopup?.isUserSubscribed) {
      setShowModal(false);
    }
  }, [globalPopup?.isUserSubscribed]);

  return (
    <div
      className="icon mb0"
      style={{ right: -1, zIndex: 10, top: -1, height: "fit-content" }}
      onClick={onSave}
    >
      <HeartIcon isMapCard={isMapCard} onWatchList={item?.onWatchList} />
    </div>
  );
};

export default memo(FavoriteButton);
